import { useUserStore } from '@/store/use-user-store';
import { Icon } from '@iconify/react';
import { Button, Spinner, Tooltip } from '@nextui-org/react';
import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';
import React from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import DataView from '../../components/data-view/data-view';
import ExportFiltersModal from '../../components/export-filters-modal';
import ListCard from '../../components/list-card'; // Import the new ListCard component
import DeleteConfirmationModal from '../../components/modal/DeleteConfirmationModal';
import { TableConfig } from '../../components/table/types';
import { useExportPractices } from '../../hooks/queries/export';
import useDeletePractice from '../../hooks/queries/practices/useDeletePractice';
import PracticeService from '../../services/practice-service';
import { StandardResponse } from '../../types/agent';
interface PracticeData {
  id: number;
  name: string;
  description: string;
  created_by: Record<string, string>;
  created_at: string;
  // Add other practice-specific fields here
}

// TODO: Remove tiles view and use table view only
interface PaginationState {
  page: number;
  rowsPerPage: number;
  total: number;
}

const selectPractice = (
  data: StandardResponse<PracticeData>,
  userId: number
) => {
  if (!userId) return data;
  const filteredData = data.results.filter(
    (practice) => Number(practice.created_by.id) === userId
  );
  return {
    ...data,
    results: filteredData,
  };
};

interface PracticesProps {
  title: string;
}

export default function Practices({ title }: PracticesProps) {
  const navigate = useNavigate();
  // const queryClient = useQueryClient();
  const [viewType, setViewType] = React.useState<'tile' | 'list'>('tile');
  const [deletingPracticeId, setDeletingPracticeId] = React.useState<
    number | null
  >(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);
  const [selectedPracticeToDelete, setSelectedPracticeToDelete] = React.useState<
    number | null
  >(null);
  const [isExportModalOpen, setIsExportModalOpen] = React.useState(false);

  const hasAdminPermissions = useUserStore(
    (state) => state.hasAdminPermissions
  );
  const { tenantSlug } = useParams<{ tenantSlug: string }>();

  const [pagination, setPagination] = React.useState<PaginationState>({
    page: 1,
    rowsPerPage: 100,
    total: 0,
  });
  const userId = useUserStore((state) => state.user?.id);

  // Infinite query for tile view
  const {
    data: infiniteData,
    fetchNextPage,
    hasNextPage,
    isLoading: isLoadingInfinite,
  } = useInfiniteQuery<StandardResponse<PracticeData>>({
    queryKey: ['practices-infinite'],
    initialPageParam: 1,
    queryFn: async ({ pageParam = 1 }) => {
      const response = await PracticeService.getPractices({
        filters: {
          page: pageParam,
          user_id: userId,
          page_size: 100,
        },
      });
      return response as StandardResponse<PracticeData>;
    },
    getNextPageParam: (lastPage, allPages) => {
      const nextPage = allPages.length + 1;
      const totalPages = Math.ceil(lastPage.count / 100);
      return nextPage > totalPages ? undefined : nextPage;
    },
    enabled: viewType === 'tile' && !!userId,
  });

  // Regular query for table view
  const {
    data: tableData,
    isLoading: isLoadingTable,
    isError,
    isFetching: isFetchingData,
  } = useQuery<StandardResponse<PracticeData>>({
    queryKey: ['practices', pagination.page, pagination.rowsPerPage],
    queryFn: async () => {
      const response = await PracticeService.getPractices({
        filters: {
          page: pagination.page,
          page_size: pagination.rowsPerPage,
          user_id: userId,
        },
      });
      return response as StandardResponse<PracticeData>;
    },
    select: (data) => selectPractice(data, Number(userId)),
    placeholderData: keepPreviousData,
    enabled: viewType === 'list' && !!userId,
  });

  // Use appropriate data based on view type
  const displayData =
    viewType === 'tile'
      ? infiniteData?.pages[0]?.results || []
      : tableData?.results || [];
  const isLoading = viewType === 'tile' ? isLoadingInfinite : isLoadingTable;

  React.useEffect(() => {
    if (tableData?.count !== undefined) {
      setPagination((prev) => ({
        ...prev,
        total: tableData.count,
      }));
    }
  }, [tableData?.count]);

  // React.useEffect(() => {
  //   const totalPages = Math.ceil(pagination.total / pagination.rowsPerPage);

  //   // Prefetch next page if available
  //   if (pagination.page < totalPages) {
  //     const nextPage = pagination.page + 1;
  //     queryClient.prefetchQuery({
  //       queryKey: ['practices', nextPage, pagination.rowsPerPage],
  //       queryFn: () =>
  //         PracticeService.getPractices({
  //           filters: {
  //             page: nextPage,
  //             page_size: pagination.rowsPerPage,
  //           },
  //         }),
  //       select: (data) => selectPractice(data, userId),
  //     });
  //   }

  //   // Prefetch current page if not in cache
  //   queryClient.prefetchQuery({
  //     queryKey: ['practices', pagination.page, pagination.rowsPerPage],
  //     queryFn: () =>
  //       PracticeService.getPractices({
  //         filters: {
  //           page: pagination.page,
  //           page_size: pagination.rowsPerPage,
  //         },
  //       }),
  //     select: (data) => selectPractice(data, userId),
  //   });
  // }, [pagination.total, pagination.page, pagination.rowsPerPage, queryClient]);

  const deletePracticeMutation = useDeletePractice();
  const exportPractices = useExportPractices();

  const handlePaginationChange = React.useCallback(
    (page: number, rowsPerPage: number) => {
      setPagination((prev) => ({
        ...prev,
        page,
        rowsPerPage,
      }));
    },
    []
  );

  const handleDelete = (practiceId: number) => {
    setSelectedPracticeToDelete(practiceId);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    if (selectedPracticeToDelete) {
      setDeletingPracticeId(selectedPracticeToDelete);
      deletePracticeMutation.mutate(selectedPracticeToDelete, {
        onSuccess: () => {
          toast.success('Practice deleted successfully');
          setDeletingPracticeId(null);
          setIsDeleteModalOpen(false);
          setSelectedPracticeToDelete(null);
        },
        onError: (error: any) => {
          toast.error(
            `Failed to delete practice ${error?.message ? error.message : ''}`
          );
          setDeletingPracticeId(null);
          setIsDeleteModalOpen(false);
          setSelectedPracticeToDelete(null);
        },
      });
    }
  };

  const renderCard = (practice: PracticeData) => (
    <ListCard
      key={practice.id}
      title={practice.name}
      description={practice.description}
      onDelete={() => handleDelete(practice.id)}
      onStart={() =>
        navigate(
          userId
            ? `/${tenantSlug}/practices/${practice.id}/run/?action=${encodeURIComponent(practice.name)}&id=${practice.id}`
            : `/${tenantSlug}/practices/${practice.id}/run/?action=${encodeURIComponent(practice.name)}&id=${practice.id}`
        )
      }
      isDeleting={deletingPracticeId === practice.id}
      subtitle={`Created by ${practice.created_by.username} on ${new Date(practice.created_at).toLocaleString()}`}
    />
  );

  const practiceTableConfig: TableConfig = {
    columns: [
      {
        uid: 'id',
        name: 'ID',
        sortable: true,
        width: '100px',
      },
      {
        uid: 'name',
        name: 'Name',
        sortable: true,
      },
      //   {
      //     uid: "description",
      //     name: "Description",
      //     sortable: true,
      //     width: "40%",
      //     renderCell: (practice: PracticeData) => (
      //       <div className="truncate max-w-md">{practice.description}</div>
      //     ),
      //   },
      {
        uid: 'created_by',
        name: 'Created By',
        renderCell: (practice: PracticeData) => (
          <span>{practice.created_by.username}</span>
        ),
      },
      {
        uid: 'created_at',
        name: 'Created At',
        renderCell: (practice: PracticeData) => (
          <span>{new Date(practice.created_at).toLocaleString()}</span>
        ),
      },
      {
        uid: 'actions',
        name: 'Actions',
        renderCell: (practice: PracticeData) => (
          <div className="flex gap-2">
            <Tooltip content="Start Practice">
              <Button
                isIconOnly
                variant="light"
                size="sm"
                onClick={() =>
                  navigate(
                    userId
                      ? `/${tenantSlug}/practices/${practice.id}/run/?action=${encodeURIComponent(practice.name)}&id=${practice.id}`
                      : `/${tenantSlug}/practices/${practice.id}/run/?action=${encodeURIComponent(practice.name)}&id=${practice.id}`
                  )
                }
                aria-label="Start"
              >
                <Icon icon="proicons:play" width="20" />
              </Button>
            </Tooltip>
            <Tooltip content="Delete">
              <Button
                isIconOnly
                variant="light"
                size="sm"
                color="danger"
                onClick={() => handleDelete(practice.id)}
                disabled={deletingPracticeId === practice.id}
                aria-label="Delete"
              >
                {deletingPracticeId === practice.id ? (
                  <Icon
                    icon="lucide:loader-circle"
                    width="20"
                    className="animate-spin"
                  />
                ) : (
                  <Icon icon="proicons:delete" width="20" />
                )}
              </Button>
            </Tooltip>
          </div>
        ),
      },
    ],
    initialVisibleColumns: [
      'id',
      'name',
      'description',
      'created_by',
      'created_at',
      'actions',
    ],
    searchable: true,
    searchPlaceholder: 'Search practices...',
    searchKey: 'name',
    pagination: {
      enabled: true,
      initialRowsPerPage: 100,
      rowsPerPageOptions: [],
      showControls: true,
      showShadow: true,
      total: pagination.total,
      page: pagination.page,
      onPageChange: (page) =>
        handlePaginationChange(page, pagination.rowsPerPage),
    },
    title: 'Practices',
    description: 'Practice History',
    stickyHeader: true,
    maxHeight: '70vh',
    bottomContentPlacement: 'outside',
    withoutBorder: false,
  };

  if (isLoading)
    return (
      <div className="flex h-full min-h-0 flex-1 items-center justify-center">
        <Spinner size="lg" />
      </div>
    );

  if (isError)
    return (
      <div className="text-center text-danger">
        Error loading practices. Please try again later.
      </div>
    );

  return (
    <>
      <DataView<PracticeData>
        title={title}
        data={displayData}
        tableConfig={practiceTableConfig}
        viewType={viewType}
        onViewChange={setViewType}
        renderCard={renderCard}
        isLoading={isFetchingData}
        infiniteScroll={{
          hasMore: hasNextPage || false,
          next: fetchNextPage,
          totalCount: infiniteData?.pages[0]?.count || 0,
        }}
        actions={
          import.meta.env.VITE_ENABLE_ANALYTICS_EXPORT === 'true' &&
          hasAdminPermissions && (
            <Button
              color="primary"
              variant="solid"
              startContent={
                <Icon icon="heroicons:document-arrow-down" width="20" />
              }
              onClick={() => setIsExportModalOpen(true)}
              isLoading={exportPractices.isPending}
            >
              Export Usage
            </Button>
          )
        }
      />
      <ExportFiltersModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        onExport={(filters) => {
          exportPractices.mutate(filters);
          setIsExportModalOpen(false);
        }}
        isLoading={exportPractices.isPending}
        title="Practices"
        type="practices"
      />
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Practice"
        entityName="practice"
        isLoading={deletingPracticeId === selectedPracticeToDelete}
      />
    </>
  );
}
